// Dream ↔ waking connections.
//
// The nightly pipeline looks for dreams that appear to be working on something the person wrote about
// awake, and records the pair with a short written reason. It is the most particular thing the app
// produces and until now nothing displayed it.
//
// The endpoint returns entry ids, not titles, so a link is only presentable once its ends have been
// matched against the decrypted entries the caller already holds — see `resolveLinks`.
//
// A dream can land on one of two things: another entry, or a recurring theme. Both have always been
// generated and stored; the browser used to read only the first kind, and quietly threw the other away
// twice over — once by never carrying `to_canonical_id` off the wire, then again by requiring a
// destination entry before a thread could be shown. iOS has rendered both all along.

import { apiGet } from './client';
import { openCipher } from './cipher';
import { patternLabel } from './labels';

/**
 * How the two entries relate. The pipeline only emits these two, and the UI needs a human phrase for
 * each; an unrecognised value falls back to a neutral label rather than showing a raw enum.
 */
export type LinkRelation = 'dream_processes_waking' | 'dream_touches_theme';

const RELATION_LABEL: Record<string, string> = {
  dream_processes_waking: 'worked through',
  dream_touches_theme: 'same theme',
};

export function relationLabel(relation: string): string {
  return RELATION_LABEL[relation] ?? 'connected';
}

export interface EntryLink {
  id: string;
  fromEntryId: string;
  toEntryId: string | null;
  /** Set instead of `toEntryId` when the dream lands on a recurring theme rather than an entry. */
  toCanonicalId: string | null;
  relation: string;
  /** Why the pipeline thinks these two belong together, decrypted. */
  explanation: string;
  confidence: number;
  createdAt: string | null;
}

interface LinkRow {
  id: string;
  from_entry_id: string;
  to_entry_id?: string | null;
  to_canonical_id?: string | null;
  relation?: string | null;
  explanation_cipher?: string | null;
  confidence?: number | null;
  created_at?: string | null;
}

export interface FetchLinksOptions {
  /** How many rows to ask for. The dashboard wants a handful; the history screen wants everything. */
  limit?: number;
  signal?: AbortSignal;
}

/** Newest first, as the endpoint returns them. */
export async function fetchLinks(dek: CryptoKey, options: FetchLinksOptions = {}): Promise<EntryLink[]> {
  const path = options.limit ? `/api/links?limit=${options.limit}` : '/api/links';
  const res = await apiGet<{ links: LinkRow[] }>(path, options.signal);
  const decrypted = await Promise.all(
    (res.links ?? []).map(async (row) => ({
      id: row.id,
      fromEntryId: row.from_entry_id,
      toEntryId: row.to_entry_id ?? null,
      toCanonicalId: row.to_canonical_id ?? null,
      relation: row.relation ?? '',
      explanation: await openCipher(dek, row.explanation_cipher),
      confidence: typeof row.confidence === 'number' ? row.confidence : 0,
      createdAt: row.created_at ?? null,
    })),
  );
  // The explanation *is* the content of the card. Without it there is a line between two titles and
  // no reason given, which reads as a bug rather than an insight.
  return decrypted.filter((l) => l.explanation.trim().length > 0);
}

/** The minimum an entry has to expose for a link to be rendered. Structurally a subset of `Entry`. */
export interface LinkableEntry {
  id: string;
  title: string;
  createdAt: Date;
  kind: string;
}

/**
 * Where a thread lands.
 *
 * Kept as a union rather than an entry-or-null because the two ends are not the same kind of thing.
 * An entry is somewhere you can go, and reads as a title and a date. A theme is a name the pipeline
 * has been tracking across many entries, and has neither. Collapsing them would mean rendering one of
 * them wrongly.
 */
export type ThreadEnd =
  | { kind: 'entry'; entry: LinkableEntry }
  | { kind: 'theme'; canonicalId: string; label: string };

export interface ResolvedLink extends EntryLink {
  from: LinkableEntry;
  to: ThreadEnd;
}

/**
 * Name both ends of each link, dropping any we cannot.
 *
 * Links can outlive their entries: deleting an entry leaves rows here pointing at nothing, and the
 * caller only holds the most recent page of entries anyway. A link whose end we cannot label is
 * unrenderable, so it is discarded rather than shown with a placeholder — a thread drawn to a blank
 * reads as a bug in the app rather than a gap in the data.
 *
 * A theme end needs no lookup, only a readable name, so those survive whatever else is loaded.
 */
export function resolveLinks(links: EntryLink[], entries: LinkableEntry[]): ResolvedLink[] {
  const byId = new Map(entries.map((e) => [e.id, e]));

  const seen = new Set<string>();
  return links.flatMap((link) => {
    const from = byId.get(link.fromEntryId);
    if (!from) return [];

    const to = resolveEnd(link, byId);
    if (!to) return [];
    // A link from an entry to itself carries no insight and looks like a rendering fault.
    if (to.kind === 'entry' && to.entry.id === from.id) return [];
    // Keep the newest explanation for each directed pair and relation. Old rows remain sealed.
    const key = JSON.stringify([from.id, to.kind, to.kind === 'entry' ? to.entry.id : to.canonicalId, link.relation]);
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ ...link, from, to }];
  });
}

function resolveEnd(link: EntryLink, byId: Map<string, LinkableEntry>): ThreadEnd | null {
  if (link.toEntryId) {
    const entry = byId.get(link.toEntryId);
    return entry ? { kind: 'entry', entry } : null;
  }
  if (link.toCanonicalId) {
    // `patternLabel` returns '' for a canonical id it cannot make words out of, which is the one case
    // where a theme end is as unrenderable as a missing entry.
    const label = patternLabel(link.toCanonicalId);
    return label ? { kind: 'theme', canonicalId: link.toCanonicalId, label } : null;
  }
  return null;
}
